import {Injectable} from '@angular/core';
import {catchError, EMPTY, filter, Observable, of, switchMap, tap} from 'rxjs';
import { ContractModel, ContractRoadmapModel, ContractStepModel, ContractWrapperModel } from '../../../models/contract.model';
import { ContractsService } from '../../../services/api/contracts.service';
import { AlertService } from '../../../services/notify/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogsMTService } from '../../../services/modal/dialogs-mt.service';
import { Router } from '@angular/router';
import { ContractDataService } from './contract-data.service';
import { ContractLoadService } from './contract-load.service';
import { ContractRoadMapService } from './contract-roadmap.service';
import { MemberModel } from '../../../models/user.model';
import { ContractAutoUpdateService } from './contract-autoupdate.service';
import { IContractDataModel } from '../models/contract-internal-models';

@Injectable({
    providedIn: 'root'
})
export class ContractActionsMainService {
  constructor(
    private contractsService:ContractsService,
    private contractLoadService:ContractLoadService,
    private contractRoadMapService:ContractRoadMapService,
    private contractDataService:ContractDataService,
    private contractAutoUpdateService:ContractAutoUpdateService,
    private router:Router,
    private alertService:AlertService,
    private translateService:TranslateService,
    private dialogsMTService:DialogsMTService,
  ) {}
    cloneContract (contractData: IContractDataModel):Observable<{ contract: ContractWrapperModel|null}> {
       if (this.contractDataService.subContractEdited.value) {
            this.alertService.setAlert(this.translateService.instant('ALERTS.COPY_CONTRACT'), 'blue');
            return of({contract:null});
      }
      return this.dialogsMTService.confirmDialog({
            actionHeader: 'CONFIRM.HEADERS.ATTENTION',
            actionBody: 'CONFIRM.TEXT.COPY_CONTRACT'
        }).pipe(
            filter(res => res.isConfirmed),
            tap(()=>this.contractAutoUpdateService.setAutoReloadInterval(contractData, false)),
            switchMap(() => this.contractsService.clone(contractData?.contract?.contract.guid).pipe(
              catchError(e => {
                this.alertService.setAlert(e.message, 'blue');
                this.contractAutoUpdateService.setAutoReloadInterval(contractData, true)
                return EMPTY
              }))),
            tap(res=>{
              this.alertService.setAlert(this.translateService.instant('ALERTS.SUCCESS_COPY_CONTRACT'), 'nd_color');
              this.router.navigate(['/account', 'contracts', 'detail', res.contract.contract.guid]);
            }),
            catchError(() => {
                return EMPTY
            })
        )
    }
    renegotiateContract(contractData: IContractDataModel): void {
      let actionHeader = this.translateService.instant('CONFIRM.HEADERS.ATTENTION');
      let actionBody =  this.translateService.instant('CONFIRM.TEXT.RENEW');
      this.dialogsMTService.confirmDialog({actionHeader:actionHeader,actionBody:actionBody})
      .pipe(
        tap(res=>res.isConfirmed !== true ?this.contractDataService.subContractFormState.next('Default') :null),
        filter(res => res.isConfirmed),
        tap(()=>this.contractAutoUpdateService.setAutoReloadInterval(contractData, false)),
        switchMap(()=> this.contractsService.renew(contractData.contract?.contract.guid)),
        (catchError(e=>{
          this.alertService.setAlert(e.message,'red')
          this.contractDataService.subContractFormState.next('Default')
          return EMPTY
        })))
      .subscribe(data=>{
        this.alertService.setAlert(this.translateService.instant('ALERTS.SIGN_CONTRACT_SUCCESS'), 'nd_color');
        this.router.navigate(['/account', 'contracts', 'detail', data.contract.contract.guid]);
      })
    }
    deleteContract (contract: ContractModel,currentMember:MemberModel) {
      let messageToConfirm = contract.creator_guid === currentMember.guid? 'CONFIRM.TEXT.DELETE_CONTRACT_SURE' : 'CONFIRM.TEXT.LEAVE_CONTRACT_SURE'
      this.dialogsMTService.confirmDialog({
          actionHeader: messageToConfirm,
          actionBody: ''
      }).pipe(
          filter(res => res.isConfirmed),
          switchMap(() => this.contractsService.deleteContract(contract.guid).pipe(
            tap(()=>this.contractDataService.subContractDeleted.next({guid:contract.guid})),
            catchError(e => {
              this.alertService.setAlert(e.message, 'blue');
              return EMPTY
            }))),
          catchError(() => {
              return EMPTY
          })
      ).subscribe(res => {
          this.alertService.setAlert(res.message, 'nd_color');
          this.router.navigate(['account','contracts','all','all_statuses'])
      })
    }
    saveContract(toSave:boolean,contractGuid:string, contractStatus:string, contractRoadmap?: ContractRoadmapModel|null, ) {
      this.contractDataService.subContractFormState.next('Submitting')
        toSave===true && contractRoadmap?
            this.contractRoadMapService.onRoadMapSave(contractRoadmap, contractGuid, contractStatus)
            :this.contractLoadService.onLoadingContract(contractGuid||'', undefined, false)
    }
    signContract(roadmap:ContractStepModel[],isVerified:boolean,guid:string,original_contract?:any):Observable<{ contract: ContractWrapperModel|null }> {
      if ( this.contractDataService.subContractEdited.value) {
        this.alertService.setAlert(this.translateService.instant('ALERTS.SIGN_CONTRACT'), 'blue');
        return of({contract:null});
      }
      const unSuccessPayments = roadmap.filter(step =>
          (step.target_type === 'payment' || step.target_type === 'multi_payment' || step.target_type === 'direct_payment') &&
          !step.completed_at);

      let confirmMessage = isVerified ? this.translateService.instant('CONFIRM.TEXT.RECALL_CONTRACT') : this.translateService.instant('CONFIRM.TEXT.SIGN_CONTRACT');
      if (!unSuccessPayments?.length && original_contract) {
          confirmMessage += this.translateService.instant('CONFIRM.TEXT.WARNING_CONTRACT');
      }
      let pinMessage = this.translateService.instant(isVerified? 'MODALS.HEADERS.SIGN_CONTRACT' : 'MODALS.HEADERS.RECALL_CONTRACT');
      return this.dialogsMTService.confirmDialog({actionHeader: 'CONFIRM.HEADERS.ATTENTION',actionBody: confirmMessage})
      .pipe(
        tap(res=>res.isConfirmed !== true ? this.contractDataService.subContractFormState.next('Default') :null),
        filter(res => res.isConfirmed),
        switchMap(()=>this.dialogsMTService.openPinDialog(!isVerified,pinMessage)),
        tap(res=>res.pin == null ? this.contractDataService.subContractFormState.next('Default') :null),
        filter(res => res.pin !== null),
        switchMap(res=>this.contractsService.signature(!isVerified, guid, res.pin.toString()).pipe(
          catchError(e => {
            this.alertService.setAlert(e.message, 'blue');
            this.contractDataService.subContractFormState.next('Default')
            return EMPTY
          }))),
        tap(()=>{
          this.alertService.setAlert(this.translateService.instant('ALERTS.SUCCESS'), 'nd_color');
          this.contractDataService.subContractFormState.next('Default')
          this.contractLoadService.onLoadingContract(guid, undefined, false)
        }),
        catchError(() => {
          this.contractDataService.subContractFormState.next('Default')
          return EMPTY
        })
      )
    }


  }